import { useState, useCallback, useEffect, useRef } from 'react';
import { traceMulliganClient, socketMeta } from './mulliganClientTrace';

/**
 * Resolve player state when userId type (string/number) differs from state keys.
 * @param {object|null|undefined} gameState
 * @param {number|string} userId
 * @returns {object|null}
 */
export function resolvePlayerStateInGameState(gameState, userId) {
  if (!gameState?.state || userId == null) {
    return null;
  }
  if (gameState.state[userId] != null) {
    return gameState.state[userId];
  }
  const uid = String(userId);
  for (const key of Object.keys(gameState.state)) {
    if (String(key) === uid) {
      return gameState.state[key];
    }
  }
  return null;
}

/**
 * Custom hook for managing Mulligan state and actions
 * @param {WebSocket|null} ws - React state socket (may be briefly null during reconnect)
 * @param {string} gameId
 * @param {number|string} userId
 * @param {() => WebSocket|null} [getActiveWebSocket] - Prefer open socket from ref + state (BattleUI)
 * @param {number} [gameSocketReadySignal] - Increments when game WebSocket reaches OPEN (same ref, new readyState)
 */
export const useMulligan = (ws, gameId, userId, getActiveWebSocket, gameSocketReadySignal = 0) => {
  const [mulliganOrders, setMulliganOrders] = useState([]);
  const [needsMulligan, setNeedsMulligan] = useState(false);
  const [mulliganTimeLeft, setMulliganTimeLeft] = useState(null);
  const mulliganStartTimeRef = useRef(null);
  const hasAutoSubmittedRef = useRef(false);
  const currentGameStateRef = useRef(null);
  const submissionInFlightRef = useRef(false);
  const pendingMulliganSelectionRef = useRef(null);
  const postSubmitRecoveryTimerRef = useRef(null);
  const lastMulliganOrderIdsRef = useRef('');

  const mulliganOrderListSignature = (orderList) =>
    (orderList || []).map((order) => order?.instanceId).filter(Boolean).join('\0');

  useEffect(() => {
    hasAutoSubmittedRef.current = false;
    submissionInFlightRef.current = false;
    pendingMulliganSelectionRef.current = null;
    mulliganStartTimeRef.current = null;
    lastMulliganOrderIdsRef.current = '';
    if (postSubmitRecoveryTimerRef.current) {
      clearTimeout(postSubmitRecoveryTimerRef.current);
      postSubmitRecoveryTimerRef.current = null;
    }
    setMulliganOrders([]);
    setNeedsMulligan(false);
    setMulliganTimeLeft(null);
  }, [gameId]);

  const resolveOpenSocket = useCallback(() => {
    const activeWs =
      typeof getActiveWebSocket === 'function' ? getActiveWebSocket() : ws;
    const wsOpen = typeof WebSocket !== 'undefined' ? WebSocket.OPEN : 1;
    if (!activeWs || activeWs.readyState !== wsOpen) {
      return null;
    }
    return activeWs;
  }, [ws, getActiveWebSocket]);

  const flushPendingMulligan = useCallback(() => {
    if (pendingMulliganSelectionRef.current === null || !needsMulligan) {
      return false;
    }
    const selection = pendingMulliganSelectionRef.current;
    const activeWs = resolveOpenSocket();
    if (!activeWs) {
      return false;
    }
    traceMulliganClient('flush_pending', {
      gameId,
      replaceCount: Array.isArray(selection) ? selection.length : null,
      socket: socketMeta(activeWs),
    });
    return submitMulliganInternalRef.current(selection);
  }, [needsMulligan, gameId, resolveOpenSocket]);

  const submitMulliganInternalRef = useRef(() => false);

  const submitMulliganInternal = useCallback((selectedOrderInstanceIds) => {
    if (!gameId) {
      traceMulliganClient('rejected', { reason: 'missing_game_id', userId });
      throw new Error('submitMulligan: gameId is required');
    }

    const activeWs = resolveOpenSocket();
    if (!activeWs) {
      pendingMulliganSelectionRef.current = selectedOrderInstanceIds;
      traceMulliganClient('queued', {
        gameId,
        userId,
        reason: 'socket_not_open',
        wsState: socketMeta(ws),
        refState: socketMeta(typeof getActiveWebSocket === 'function' ? getActiveWebSocket() : null),
      });
      return false;
    }

    if (submissionInFlightRef.current) {
      traceMulliganClient('rejected', { reason: 'submission_in_flight', gameId, userId });
      return false;
    }

    const gameState = currentGameStateRef.current;
    const playerState = resolvePlayerStateInGameState(gameState, userId);

    if (!playerState) {
      pendingMulliganSelectionRef.current = selectedOrderInstanceIds;
      traceMulliganClient('queued', {
        reason: 'player_state_missing',
        gameId,
        userId,
        stateKeys: gameState?.state ? Object.keys(gameState.state) : null,
      });
      return false;
    }

    const backendNeedsMulligan = playerState.needsMulligan === true;
    const hasAlreadyMulliganed = playerState.hasMulliganed === true;
    const needsTeamLeaderMulligan = playerState.needsTeamLeaderMulligan === true;
    const backendAllowsMulligan = (backendNeedsMulligan && !hasAlreadyMulliganed) || needsTeamLeaderMulligan;

    if (!backendAllowsMulligan) {
      traceMulliganClient('rejected', {
        reason: 'backend_disallows_mulligan',
        gameId,
        userId,
        needsMulligan: backendNeedsMulligan,
        hasMulliganed: hasAlreadyMulliganed,
        needsTeamLeaderMulligan,
      });
      if (hasAlreadyMulliganed) {
        hasAutoSubmittedRef.current = true;
        mulliganStartTimeRef.current = null;
        setNeedsMulligan(false);
        pendingMulliganSelectionRef.current = null;
      }
      return false;
    }

    const finalPlayerState = resolvePlayerStateInGameState(currentGameStateRef.current, userId);
    if (!finalPlayerState) {
      pendingMulliganSelectionRef.current = selectedOrderInstanceIds;
      traceMulliganClient('queued', { reason: 'final_player_state_missing', gameId, userId });
      return false;
    }

    const finalAllows =
      (finalPlayerState.needsMulligan === true && !finalPlayerState.hasMulliganed) ||
      (finalPlayerState.needsTeamLeaderMulligan === true);
    if (!finalAllows) {
      traceMulliganClient('rejected', { reason: 'final_backend_disallows', gameId, userId });
      if (finalPlayerState.hasMulliganed === true) {
        hasAutoSubmittedRef.current = true;
        mulliganStartTimeRef.current = null;
        setNeedsMulligan(false);
        pendingMulliganSelectionRef.current = null;
      }
      return false;
    }

    submissionInFlightRef.current = true;
    if (postSubmitRecoveryTimerRef.current) {
      clearTimeout(postSubmitRecoveryTimerRef.current);
      postSubmitRecoveryTimerRef.current = null;
    }
    try {
      const payload = {
        type: 'play-turn',
        gameId,
        action: {
          type: 'mulligan',
          selectedOrderInstanceIds: selectedOrderInstanceIds,
        },
      };
      activeWs.send(JSON.stringify(payload));
      traceMulliganClient('sent', {
        gameId,
        userId,
        replaceCount: Array.isArray(selectedOrderInstanceIds) ? selectedOrderInstanceIds.length : null,
        socket: socketMeta(activeWs),
      });
      pendingMulliganSelectionRef.current = null;
      postSubmitRecoveryTimerRef.current = setTimeout(() => {
        postSubmitRecoveryTimerRef.current = null;
        const latestPlayer = resolvePlayerStateInGameState(currentGameStateRef.current, userId);
        const stillWaiting =
          latestPlayer &&
          ((latestPlayer.needsMulligan === true && latestPlayer.hasMulliganed !== true) ||
            latestPlayer.needsTeamLeaderMulligan === true);
        if (stillWaiting) {
          traceMulliganClient('recovery_retry_enabled', { gameId, userId });
          submissionInFlightRef.current = false;
          hasAutoSubmittedRef.current = false;
        } else {
          submissionInFlightRef.current = false;
        }
      }, 2500);
      return true;
    } catch (error) {
      traceMulliganClient('send_failed', {
        gameId,
        userId,
        message: error instanceof Error ? error.message : String(error),
      });
      console.error('🔄 Error submitting mulligan:', error);
      submissionInFlightRef.current = false;
      pendingMulliganSelectionRef.current = selectedOrderInstanceIds;
      return false;
    }
  }, [gameId, userId, ws, getActiveWebSocket, resolveOpenSocket]);

  submitMulliganInternalRef.current = submitMulliganInternal;

  const updateMulliganOrders = useCallback((gameState) => {
    currentGameStateRef.current = gameState;

    const playerState = resolvePlayerStateInGameState(gameState, userId);
    if (!playerState) {
      setMulliganOrders([]);
      setNeedsMulligan(false);
      mulliganStartTimeRef.current = null;
      hasAutoSubmittedRef.current = false;
      submissionInFlightRef.current = false;
      pendingMulliganSelectionRef.current = null;
      return;
    }

    if (playerState.hasMulliganed && !playerState.needsMulligan && !playerState.needsTeamLeaderMulligan) {
      submissionInFlightRef.current = false;
      pendingMulliganSelectionRef.current = null;
      hasAutoSubmittedRef.current = true;
      if (postSubmitRecoveryTimerRef.current) {
        clearTimeout(postSubmitRecoveryTimerRef.current);
        postSubmitRecoveryTimerRef.current = null;
      }
    }

    const shouldMulligan =
      (playerState.needsMulligan === true && playerState.hasMulliganed === false) ||
      (playerState.needsTeamLeaderMulligan === true);

    if (shouldMulligan && !mulliganStartTimeRef.current) {
      mulliganStartTimeRef.current = Date.now();
      hasAutoSubmittedRef.current = false;
    } else if (!shouldMulligan) {
      mulliganStartTimeRef.current = null;
      hasAutoSubmittedRef.current = false;
      setMulliganTimeLeft(null);
    }

    setNeedsMulligan(shouldMulligan);

    if (shouldMulligan) {
      let nextOrders = [];
      if (playerState.needsTeamLeaderMulligan === true && playerState.teamLeaderMulliganOrders) {
        nextOrders =
          playerState.hand?.filter(
            (order) => order && playerState.teamLeaderMulliganOrders.includes(order.instanceId)
          ) || [];
      } else if (gameState.turnNumber === 1) {
        nextOrders = playerState.hand?.slice(0, 3) || [];
      }
      const signature = mulliganOrderListSignature(nextOrders);
      if (signature !== lastMulliganOrderIdsRef.current) {
        lastMulliganOrderIdsRef.current = signature;
        setMulliganOrders([...nextOrders]);
      }
    } else {
      if (lastMulliganOrderIdsRef.current !== '') {
        lastMulliganOrderIdsRef.current = '';
        setMulliganOrders([]);
      }
    }
  }, [userId]);

  const submitMulligan = useCallback(
    async (selectedOrderInstanceIds) => {
      traceMulliganClient('click', {
        gameId,
        userId,
        replaceCount: Array.isArray(selectedOrderInstanceIds) ? selectedOrderInstanceIds.length : null,
      });
      const sent = submitMulliganInternal(selectedOrderInstanceIds);
      if (!sent) {
        flushPendingMulligan();
      }
    },
    [submitMulliganInternal, gameId, userId, flushPendingMulligan]
  );

  // Flush when game socket becomes OPEN (readyState change does not change ws reference)
  useEffect(() => {
    flushPendingMulligan();
  }, [gameSocketReadySignal, ws, gameId, needsMulligan, flushPendingMulligan]);

  // Safety net: retry pending submit while mulligan UI is open
  useEffect(() => {
    if (!needsMulligan || pendingMulliganSelectionRef.current === null) {
      return undefined;
    }
    const interval = setInterval(() => {
      if (pendingMulliganSelectionRef.current !== null) {
        flushPendingMulligan();
      }
    }, 400);
    return () => clearInterval(interval);
  }, [needsMulligan, gameSocketReadySignal, flushPendingMulligan]);

  useEffect(() => {
    if (!needsMulligan || !mulliganStartTimeRef.current) {
      setMulliganTimeLeft(null);
      return;
    }

    const gameState = currentGameStateRef.current;
    const mulliganDuration =
      gameState?.mulliganDurationMs !== undefined && gameState?.mulliganDurationMs !== null
        ? gameState.mulliganDurationMs
        : 20000;
    const warningTime = 5000;

    if (mulliganDuration === 0) {
      setMulliganTimeLeft(null);
      return;
    }

    const updateTimer = () => {
      const now = Date.now();
      const elapsed = now - mulliganStartTimeRef.current;
      const remaining = mulliganDuration - elapsed;

      if (remaining <= 0) {
        const latestState = currentGameStateRef.current;
        const playerState = resolvePlayerStateInGameState(latestState, userId);
        let shouldSubmit = false;

        if (playerState) {
          const stillNeedsMulligan =
            (playerState.needsMulligan === true && playerState.hasMulliganed === false) ||
            (playerState.needsTeamLeaderMulligan === true);
          shouldSubmit =
            stillNeedsMulligan &&
            !submissionInFlightRef.current &&
            !hasAutoSubmittedRef.current &&
            mulliganStartTimeRef.current;
        }

        if (shouldSubmit) {
          submitMulliganInternal([]);
        }
        setMulliganTimeLeft(null);
        return;
      }

      if (remaining <= warningTime) {
        setMulliganTimeLeft(Math.ceil(remaining / 1000));
      } else {
        setMulliganTimeLeft(null);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [needsMulligan, submitMulliganInternal, userId]);

  useEffect(() => {
    return () => {
      if (postSubmitRecoveryTimerRef.current) {
        clearTimeout(postSubmitRecoveryTimerRef.current);
        postSubmitRecoveryTimerRef.current = null;
      }
    };
  }, []);

  return {
    mulliganOrders,
    needsMulligan,
    mulliganTimeLeft,
    updateMulliganOrders,
    submitMulligan,
  };
};

export default useMulligan;
